import { clsx } from 'clsx';
import type { CSSProperties, SyntheticEvent } from 'react';
import { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react';
import { Divider } from '../Divider';
import { List } from './Components/List';
import ConfirmationMenuItem from './ConfirmationMenuItem';
import GroupMenuItem from './GroupMenuItem';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import MenuItem from './MenuItem';
import type {
  ConfirmMenuItem,
  GroupMenuItem as GroupMenuItemType,
  ListMenuItem,
  MenuListItem,
} from './types';

// re-export types for backward compatibility
export type { ConfirmMenuItem, GroupMenuItemType as GroupMenuItem, ListMenuItem, MenuListItem };

export type MenuListProps = {
  items: MenuListItem[];
  onAction: (id: string, event: SyntheticEvent) => void;
  className?: string;
  menuContentContainerClassName?: string;
  size?: 'sm' | 'md';
  style?: CSSProperties;
  slots?: {
    menuContentItem?: React.ElementType;
  };
  /** Enable arrow key navigation and selection via Enter */
  enableKeyboardNavigation?: boolean;
  /** Class applied to the keyboard-active item; defaults to `bg-secondary_hover` */
  activeItemClassName?: string;
  defaultActiveIndex?: number;
  /** Scrolls the menu so the selected item is visible */
  autoScrollSelectedItem?: boolean;

  /** Props to be passed to the list element to handle scroll events
   * when rendered in a bottom sheet in mobile device */
  onWheel?: (event: React.WheelEvent) => void;
  onTouchMove?: (event: React.TouchEvent) => void;
};

const MenuList = forwardRef<HTMLUListElement, MenuListProps>(function MenuList(props, _ref) {
  const {
    items,
    onAction,
    className,
    size = 'md',
    slots,
    enableKeyboardNavigation = true,
    activeItemClassName = 'bg-secondary_hover',
    defaultActiveIndex,
    autoScrollSelectedItem = false,
    menuContentContainerClassName,
    ...rest
  } = props;

  const listRef = useRef<HTMLUListElement | null>(null);
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  const handleGroupToggle = useCallback((id: string, shouldOpen: boolean) => {
    setOpenGroupId(shouldOpen ? id : null);
  }, []);

  useEffect(() => {
    if (!autoScrollSelectedItem) {
      return;
    }

    const selectedItem = listRef.current?.querySelector('[data-selected="true"]');
    if (!selectedItem) {
      return;
    }

    // adding inside raF to focus after the list is rendered
    if (selectedItem instanceof HTMLElement) {
      requestAnimationFrame(() => {
        selectedItem.scrollIntoView({ block: 'center' });
      });
    }
  }, [autoScrollSelectedItem]);

  const { activeIndex, onMouseEnter } = useKeyboardNavigation({
    enableKeyboardNavigation,
    items,
    onAction,
    defaultActiveIndex,
  });

  const itemClassName = (index: number, item: MenuListItem) =>
    clsx(item.className, {
      [activeItemClassName]: enableKeyboardNavigation && index === activeIndex,
    });

  return (
    <List className={clsx('py-xs', className)} ref={listRef} {...rest}>
      {items.map((item, index) => {
        switch (item.type) {
          case 'label':
            return (
              <MenuItem
                caption={item.caption}
                captionClassName={item.captionClassName}
                className={itemClassName(index, item)}
                disabled={item.disabled}
                enableHoverEffect={!enableKeyboardNavigation}
                // if keyboard navigation is enabled, we don't rely on :hover selector to show selected state
                endDecoratorComponent={item.endDecoratorComponent}
                endDecoratorNode={item.endDecoratorNode}
                helpTextDetails={item.helpTextDetails}
                iconClassName={item.iconClassName}
                id={item.id}
                index={index}
                key={item.id}
                menuContentContainerClassName={menuContentContainerClassName}
                onClick={(event) => {
                  event.stopPropagation();
                  onAction(item.id, event);
                }}
                onMouseEnter={() => {
                  onMouseEnter(index);
                }}
                selected={item.selected}
                size={size}
                slots={slots}
                startDecoratorComponent={item.startDecoratorComponent}
                startDecoratorNode={item.startDecoratorNode}
                title={item.title}
                titleClassName={item.titleClassName}
                variant={item.variant}
              />
            );
          case 'confirmation':
            return (
              <ConfirmationMenuItem
                caption={item.caption}
                className={itemClassName(index, item)}
                confirmationTitle={item.confirmationTitle}
                disabled={item.disabled}
                enableHoverEffect={!enableKeyboardNavigation}
                // if keyboard navigation is enabled, we don't rely on :hover selector to show selected state
                helpTextDetails={item.helpTextDetails}
                key={item.id}
                menuContentContainerClassName={menuContentContainerClassName}
                onClick={(event) => {
                  event.stopPropagation();
                  onAction(item.id, event);
                }}
                onMouseEnter={() => {
                  onMouseEnter(index);
                }}
                selected={item.selected}
                size={size}
                startDecoratorComponent={item.startDecoratorComponent}
                startDecoratorNode={item.startDecoratorNode}
                title={item.title}
                titleClassName={item.titleClassName}
                variant={item.variant}
              />
            );
          case 'group':
            return (
              <GroupMenuItem
                caption={item.caption}
                className={itemClassName(index, item)}
                disabled={item.disabled}
                enableHoverEffect={!enableKeyboardNavigation}
                // if keyboard navigation is enabled, we don't rely on :hover selector to show selected state
                endDecoratorComponent={item.endDecoratorComponent}
                endDecoratorNode={item.endDecoratorNode}
                helpTextDetails={item.helpTextDetails}
                id={item.id}
                index={index}
                isOpen={openGroupId === item.id}
                items={item.items}
                key={item.id}
                MenuListComponent={MemoisedMenuList}
                menuContentContainerClassName={menuContentContainerClassName}
                onAction={onAction}
                onMouseEnter={() => {
                  onMouseEnter(index);
                }}
                onToggle={handleGroupToggle}
                selected={item.selected}
                size={size}
                slots={slots}
                startDecoratorComponent={item.startDecoratorComponent}
                startDecoratorNode={item.startDecoratorNode}
                title={item.title}
                titleClassName={item.titleClassName}
                variant={item.variant}
              />
            );
          case 'divider':
            return (
              <Divider
                className={clsx('mx-md my-xs', item.className)}
                key={`${item.type}:${index}`}
              />
            );
          default:
            return null;
        }
      })}
    </List>
  );
});

const MemoisedMenuList = memo(MenuList);

export default MemoisedMenuList;
